import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';

const AppointmentCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    citizenName: '',
    citizenId: '',
    phoneNumber: '',
    permitType: '',
    timeSlot: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const permitTypes = [
    { value: 'firearm', label: 'อาวุธปืน' },
    { value: 'hotel', label: 'โรงแรม' },
    { value: 'gambling', label: 'การพนัน' }
  ];

  const timeSlots = [
    { value: '09:00-10:00', label: '09:00 - 10:00' },
    { value: '10:00-11:00', label: '10:00 - 11:00' },
    { value: '11:00-12:00', label: '11:00 - 12:00' },
    { value: '13:00-14:00', label: '13:00 - 14:00' },
    { value: '14:00-15:00', label: '14:00 - 15:00' }
  ];

  // ดึงข้อมูลการนัดหมายสำหรับเดือนปัจจุบัน
  const fetchAppointments = async (date) => {
    setLoading(true);
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .gte('appointment_date', startOfMonth.toISOString().split('T')[0])
        .lte('appointment_date', endOfMonth.toISOString().split('T')[0])
        .order('appointment_date', { ascending: true });

      if (error) {
        console.error('Error fetching appointments:', error);
        return;
      }

      setAppointments(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments(currentDate);
  }, [currentDate]);

  // สร้างปฏิทิน
  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const calendar = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      calendar.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return calendar;
  };

  // ตรวจสอบว่าวันนี้มีการนัดหมายหรือไม่
  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.appointment_date === dateStr);
  };

  // ตรวจสอบว่าวันนี้เป็นวันปัจจุบันหรือไม่
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // ตรวจสอบว่าวันนี้เป็นเดือนปัจจุบันหรือไม่
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // เปลี่ยนเดือน
  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // เลือกวันที่
  const handleDateSelect = (date) => {
    if (isCurrentMonth(date)) {
      setSelectedDate(date);
      setShowBookingModal(true);
      setBookingForm(prev => ({
        ...prev,
        appointmentDate: date.toISOString().split('T')[0]
      }));
    }
  };

  // ดูรายละเอียดการนัดหมาย
  const handleViewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  // สร้างเลขที่จอง
  const generateBookingNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `APT${year}${month}${day}${random}`;
  };

  // ส่ง LINE Notify
  const sendLineNotify = async (bookingData) => {
    try {
      const response = await fetch('https://notify-api.line.me/api/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${import.meta.env.VITE_LINE_NOTIFY_TOKEN}`
        },
        body: new URLSearchParams({
          message: `📢 มีการจองคิวใหม่
ชื่อ: ${bookingData.citizen_name}
ประเภท: ${getPermitTypeLabel(bookingData.permit_type)}
วันที่: ${new Date(bookingData.appointment_date).toLocaleDateString('th-TH')}
เวลา: ${bookingData.time_slot}`
        })
      });

      if (!response.ok) {
        console.error('LINE Notify failed:', response.statusText);
      }
    } catch (error) {
      console.error('LINE Notify error:', error);
    }
  };

  const getPermitTypeLabel = (type) => {
    const permit = permitTypes.find(p => p.value === type);
    return permit ? permit.label : type;
  };

  // บันทึกการจองคิว
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!bookingForm.citizenName.trim()) newErrors.citizenName = 'กรุณากรอกชื่อ-สกุล';
    if (!bookingForm.citizenId.trim()) newErrors.citizenId = 'กรุณากรอกเลขบัตรประชาชน';
    if (!bookingForm.phoneNumber.trim()) newErrors.phoneNumber = 'กรุณากรอกหมายเลขโทรศัพท์';
    if (!bookingForm.permitType) newErrors.permitType = 'กรุณาเลือกประเภทการขออนุญาต';
    if (!bookingForm.timeSlot) newErrors.timeSlot = 'กรุณาเลือกช่วงเวลา';
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const bookingNumber = generateBookingNumber();
      
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          booking_number: bookingNumber,
          citizen_name: bookingForm.citizenName.trim(),
          citizen_id: bookingForm.citizenId.trim(),
          phone_number: bookingForm.phoneNumber.trim(),
          permit_type: bookingForm.permitType,
          appointment_date: bookingForm.appointmentDate,
          time_slot: bookingForm.timeSlot,
          status: 'confirmed',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating appointment:', error);
        throw new Error('เกิดข้อผิดพลาดในการจองคิว');
      }

      // ส่ง LINE Notify
      await sendLineNotify(data);

      // รีเฟรชข้อมูล
      await fetchAppointments(currentDate);
      
      // ปิด modal และรีเซ็ตฟอร์ม
      setShowBookingModal(false);
      setBookingForm({
        citizenName: '',
        citizenId: '',
        phoneNumber: '',
        permitType: '',
        timeSlot: ''
      });
      setErrors({});
      
      alert('จองคิวสำเร็จ! เลขที่จอง: ' + bookingNumber);
    } catch (error) {
      console.error('Submission error:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ดึงข้อมูลเวลาที่จองแล้วในวันที่เลือก
  const getBookedSlots = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments
      .filter(apt => apt.appointment_date === dateStr && apt.status === 'confirmed')
      .map(apt => apt.time_slot);
  };

  const calendar = generateCalendar();
  const monthNames = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Calendar" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                  ปฏิทินจองคิวออนไลน์
                </h1>
                <p className="text-muted-foreground mt-1">
                  เลือกวันที่และเวลาที่สะดวกในการเข้ารับบริการ
                </p>
              </div>
            </div>
            
            {/* Back Button */}
            <Button
              variant="outline"
              onClick={() => navigate('/online-booking')}
              iconName="ArrowLeft"
              iconPosition="left"
              className="hidden sm:flex"
            >
              กลับไปจองคิว
            </Button>
          </div>
          
          {/* Mobile Back Button */}
          <div className="sm:hidden mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/online-booking')}
              iconName="ArrowLeft"
              iconPosition="left"
              fullWidth
            >
              กลับไปจองคิวออนไลน์
            </Button>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-card rounded-lg border border-border shadow-government p-6 mb-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => changeMonth(-1)}
                iconName="ChevronLeft"
              >
                เดือนก่อน
              </Button>
              <Button
                variant="outline"
                onClick={() => changeMonth(1)}
                iconName="ChevronRight"
              >
                เดือนถัดไป
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {calendar.map((date, index) => {
              const dayAppointments = getAppointmentsForDate(date);
              const isCurrentMonthDay = isCurrentMonth(date);
              const isTodayDate = isToday(date);
              
              return (
                <div
                  key={index}
                  className={`min-h-[80px] p-2 border border-border cursor-pointer transition-colors ${
                    isCurrentMonthDay
                      ? 'bg-background hover:bg-muted/50'
                      : 'bg-muted/30 text-muted-foreground'
                  } ${isTodayDate ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleDateSelect(date)}
                >
                  <div className="text-sm font-medium mb-1">
                    {date.getDate()}
                  </div>
                  
                  {/* Appointments */}
                  <div className="space-y-1">
                    {dayAppointments.slice(0, 2).map((apt, aptIndex) => (
                      <div
                        key={aptIndex}
                        className="text-xs p-1 bg-primary/10 text-primary rounded cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAppointment(apt);
                        }}
                      >
                        {apt.time_slot} - {getPermitTypeLabel(apt.permit_type)}
                      </div>
                    ))}
                    {dayAppointments.length > 2 && (
                      <div className="text-xs text-muted-foreground">
                        +{dayAppointments.length - 2} เพิ่มเติม
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-card rounded-lg border border-border shadow-government p-4 mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">คำอธิบาย</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-primary/10 border border-primary rounded"></div>
              <span className="text-sm text-muted-foreground">วันที่มีการนัดหมาย</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 ring-2 ring-primary rounded"></div>
              <span className="text-sm text-muted-foreground">วันนี้</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-muted/30 rounded"></div>
              <span className="text-sm text-muted-foreground">เดือนอื่น</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            onClick={() => navigate('/online-booking')}
            iconName="Calendar"
            iconPosition="left"
            className="sm:order-2"
            fullWidth
          >
            จองคิวใหม่
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/track')}
            iconName="Search"
            iconPosition="left"
            className="sm:order-1"
            fullWidth
          >
            ติดตามคำร้อง
          </Button>
        </div>
      </main>

      {/* Booking Modal */}
      {showBookingModal && selectedDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground">
                  จองคิววันที่ {selectedDate.toLocaleDateString('th-TH')}
                </h3>
                <Button variant="outline" onClick={() => setShowBookingModal(false)}>
                  <Icon name="X" size={16} />
                </Button>
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <Input
                  label="ชื่อ-สกุล"
                  type="text"
                  placeholder="กรอกชื่อ-สกุลของท่าน"
                  value={bookingForm.citizenName}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, citizenName: e.target.value }))}
                  error={errors.citizenName}
                  required
                />

                <Input
                  label="เลขบัตรประชาชน"
                  type="text"
                  placeholder="1234567890123"
                  value={bookingForm.citizenId}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, citizenId: e.target.value }))}
                  error={errors.citizenId}
                  required
                />

                <Input
                  label="หมายเลขโทรศัพท์"
                  type="tel"
                  placeholder="08X-XXX-XXXX"
                  value={bookingForm.phoneNumber}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  error={errors.phoneNumber}
                  required
                />

                <Select
                  label="ประเภทการขออนุญาต"
                  placeholder="เลือกประเภทการขออนุญาต"
                  options={permitTypes}
                  value={bookingForm.permitType}
                  onChange={(val) => setBookingForm(prev => ({ ...prev, permitType: val }))}
                  error={errors.permitType}
                  required
                />
                {bookingForm.permitType === 'firearm' && (
                  <Select
                    label="หัวข้อย่อย (อาวุธปืน)"
                    placeholder="เลือกรายการ"
                    options={[
                      { value: 'p3_purchase_permit', label: 'การขออนุญาตมีและใช้อาวุธปืนและเครื่องกระสุนปืน (ออกใบอนุญาตซื้อ ป.3)' },
                      { value: 'p4_possession_permit', label: 'การออกใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
                      { value: 'p4_transfer_endorse', label: 'การขอสลักหลังตัดโอนใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
                      { value: 'other', label: 'อื่นๆ' }
                    ]}
                    value={bookingForm.firearmSubType}
                    onChange={(val) => setBookingForm(prev => ({ ...prev, firearmSubType: val }))}
                    required
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    ช่วงเวลา
                    <span className="text-error ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {timeSlots.map((slot) => {
                      const bookedSlots = getBookedSlots(selectedDate);
                      const isBooked = bookedSlots.includes(slot.value);
                      const isSelected = bookingForm.timeSlot === slot.value;
                      
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          disabled={isBooked}
                          onClick={() => setBookingForm(prev => ({ ...prev, timeSlot: slot.value }))}
                          className={`p-3 rounded-md border text-sm transition-colors ${
                            isBooked
                              ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background hover:border-primary/60'
                          }`}
                        >
                          {slot.label}
                          {isBooked && <span className="ml-2 text-xs">(จองแล้ว)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {errors.timeSlot && (
                    <p className="text-xs text-error mt-1">{errors.timeSlot}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBookingModal(false)}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'กำลังจองคิว...' : 'จองคิว'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground">
                  รายละเอียดการนัดหมาย
                </h3>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  <Icon name="X" size={16} />
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">เลขที่จอง:</span>
                  <span className="text-sm font-medium font-mono">{selectedAppointment.booking_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">ชื่อ-สกุล:</span>
                  <span className="text-sm font-medium">{selectedAppointment.citizen_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">เบอร์โทร:</span>
                  <span className="text-sm font-medium">{selectedAppointment.phone_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">ประเภท:</span>
                  <span className="text-sm font-medium">{getPermitTypeLabel(selectedAppointment.permit_type)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">วันที่:</span>
                  <span className="text-sm font-medium">{new Date(selectedAppointment.appointment_date).toLocaleDateString('th-TH')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">เวลา:</span>
                  <span className="text-sm font-medium">{selectedAppointment.time_slot}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">สถานะ:</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${
                    selectedAppointment.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                    selectedAppointment.status === 'approved' ? 'bg-green-100 text-green-700' :
                    selectedAppointment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedAppointment.status === 'confirmed' ? 'ยืนยันแล้ว' :
                     selectedAppointment.status === 'approved' ? 'อนุมัติแล้ว' :
                     selectedAppointment.status === 'cancelled' ? 'ยกเลิก' :
                     'เสร็จสิ้น'}
                  </span>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setShowDetailModal(false)}>
                  ปิด
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;
